import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { ThemeConfigService } from '@core/theme/theme-config.service';

import { RequireFullAuthService } from './require-full-auth.service';

@Component({
  selector: 'app-password-validation-modal',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (gate.open()) {
      <div
        class="password-validation"
        role="presentation"
        aria-live="polite"
        (click)="gate.cancel('click_outside')"
      >
        <section
          #dialog
          class="password-validation__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="password-validation-title"
          aria-describedby="password-validation-description"
          tabindex="-1"
          (click)="$event.stopPropagation()"
        >
          <button
            class="password-validation__close"
            type="button"
            aria-label="Cerrar validación"
            [disabled]="gate.loading()"
            (click)="gate.cancel('close_button')"
          >
            <svg aria-hidden="true" focusable="false" viewBox="0 0 30 30">
              <path
                d="m8.5 8.5 13 13m0-13-13 13"
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-width="2"
              />
            </svg>
          </button>

          <form class="password-validation__content" (submit)="submit($event)">
            <h2 id="password-validation-title" class="password-validation__title">
              Validación de Usuario
            </h2>

            <p id="password-validation-description" class="password-validation__description">
              Para acceder a más información por favor ingrese la contraseña de su cuenta
              <strong>{{ brandName() }}</strong>
            </p>

            <div class="password-validation__field">
              <label class="sr-only" for="password-validation-input">Contraseña</label>
              <input
                #passwordInput
                id="password-validation-input"
                class="password-validation__input"
                [type]="showPassword() ? 'text' : 'password'"
                autocomplete="current-password"
                placeholder="Contraseña"
                [value]="password()"
                [disabled]="gate.loading()"
                [attr.aria-invalid]="gate.error() ? 'true' : null"
                [attr.aria-describedby]="gate.error() ? 'password-validation-error' : null"
                (input)="setPassword($event)"
              />
              <button
                class="password-validation__toggle"
                type="button"
                [attr.aria-label]="showPassword() ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                [attr.aria-pressed]="showPassword()"
                [disabled]="gate.loading()"
                (click)="showPassword.set(!showPassword())"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12Z" />
                  <circle cx="12" cy="12" r="2.6" />
                  @if (!showPassword()) {
                    <path d="M4 20 20 4" />
                  }
                </svg>
              </button>
            </div>

            <label class="password-validation__remember">
              <input
                type="checkbox"
                [checked]="remember()"
                [disabled]="gate.loading()"
                (change)="setRemember($event)"
              />
              <span>Recordar cuenta</span>
            </label>

            @if (gate.error(); as error) {
              <p id="password-validation-error" class="password-validation__error" role="alert">
                {{ error }}
              </p>
            }

            <p class="password-validation__recover">
              ¿Olvidó su contraseña?<br />
              <a routerLink="/ingreso/recuperar-clave" (click)="closeForRecovery()">
                Recupérala ahora
              </a>
            </p>

            <button class="password-validation__submit" type="submit" [disabled]="gate.loading()">
              {{ gate.loading() ? 'Validando…' : 'Confirmar' }}
            </button>
          </form>
        </section>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .password-validation {
        position: fixed;
        inset: 0;
        z-index: 12000;
        display: grid;
        place-items: center;
        padding: 20px;
        background: var(--password-validation-overlay-bg);
      }

      .password-validation__dialog {
        position: relative;
        width: min(500px, calc(100vw - 40px));
        min-height: 510px;
        padding: 64px 36px 76px 31px;
        background: var(--password-validation-surface);
        border-radius: var(--password-validation-radius);
        box-shadow: 0 24px 70px
          color-mix(in srgb, var(--password-validation-primary) 20%, transparent);
        color: var(--password-validation-text);
        outline: none;
      }

      .password-validation__close {
        position: absolute;
        top: 20px;
        right: 22px;
        width: 30px;
        height: 30px;
        display: grid;
        place-items: center;
        border: 0;
        background: transparent;
        cursor: pointer;
      }

      .password-validation__close svg {
        width: 30px;
        height: 30px;
        display: block;
      }

      .password-validation__close:disabled {
        cursor: wait;
        opacity: 0.55;
      }

      .password-validation__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 22px;
        width: 100%;
      }

      .password-validation__title {
        width: 100%;
        margin: 0 0 10px;
        font-family: var(--handel-font-family-sans);
        font-size: 22px;
        font-weight: 700;
        line-height: 30px;
        color: var(--password-validation-primary);
        text-align: center;
      }

      .password-validation__description {
        width: min(418px, 100%);
        margin: 0;
        font-family: var(--handel-font-family-sans);
        font-size: 18px;
        font-weight: 500;
        line-height: 25px;
        text-align: center;
      }

      .password-validation__description strong {
        font-weight: 700;
      }

      .password-validation__field {
        position: relative;
        width: min(418px, 100%);
      }

      .password-validation__input {
        box-sizing: border-box;
        width: 100%;
        height: 41px;
        padding: 0 56px 0 20px;
        border: 1px solid var(--password-validation-border);
        border-radius: var(--password-validation-input-radius);
        background: var(--password-validation-surface);
        color: var(--password-validation-primary);
        font-family: var(--handel-font-family-sans);
        font-size: 18px;
        font-weight: 400;
        line-height: 22px;
        outline: none;
        transition:
          border-color 160ms ease,
          box-shadow 160ms ease;
      }

      .password-validation__input::placeholder {
        color: var(--password-validation-placeholder);
      }

      .password-validation__input:focus {
        border-color: var(--password-validation-primary);
        box-shadow: 0 0 0 3px
          color-mix(in srgb, var(--password-validation-primary) 12%, transparent);
      }

      .password-validation__input[aria-invalid='true'] {
        border-color: var(--password-validation-error-border);
      }

      .password-validation__toggle {
        position: absolute;
        top: 0;
        right: 14px;
        display: grid;
        width: 34px;
        height: 41px;
        place-items: center;
        padding: 0;
        border: 0;
        background: transparent;
        color: var(--password-validation-icon);
        cursor: pointer;
      }

      .password-validation__toggle svg {
        width: 20px;
        height: 20px;
      }

      .password-validation__toggle:disabled {
        cursor: wait;
        opacity: 0.55;
      }

      .password-validation__remember {
        width: min(418px, 100%);
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: -5px;
        font-family: var(--handel-font-family-sans);
        font-size: 14px;
        font-weight: 500;
        line-height: 19px;
        color: var(--password-validation-text);
      }

      .password-validation__remember input {
        width: 20px;
        height: 20px;
        margin: 0;
        accent-color: var(--password-validation-primary);
      }

      .password-validation__error {
        width: min(418px, 100%);
        margin: -8px 0 0;
        color: var(--password-validation-error);
        font-family: var(--handel-font-family-sans);
        font-size: 13px;
        line-height: 18px;
        text-align: center;
      }

      .password-validation__recover {
        width: min(418px, 100%);
        margin: 3px 0 9px;
        font-family: var(--handel-font-family-sans);
        font-size: 18px;
        font-weight: 500;
        line-height: 25px;
        text-align: center;
      }

      .password-validation__recover a {
        color: var(--password-validation-link);
        text-decoration: underline;
      }

      .password-validation__submit {
        width: min(417px, 100%);
        height: 48px;
        border: 0;
        border-radius: var(--password-validation-radius);
        background: var(--password-validation-primary);
        color: var(--password-validation-submit-text);
        font-family: var(--handel-font-family-sans);
        font-size: 16px;
        font-weight: 600;
        line-height: 22px;
        cursor: pointer;
        transition:
          transform 160ms ease,
          box-shadow 160ms ease,
          opacity 160ms ease;
      }

      .password-validation__submit:not(:disabled):hover {
        transform: translateY(-1px);
        box-shadow: 0 10px 24px
          color-mix(in srgb, var(--password-validation-primary) 24%, transparent);
      }

      .password-validation__submit:disabled {
        cursor: wait;
        opacity: 0.72;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      @media (max-width: 560px) {
        .password-validation {
          padding: 16px;
        }

        .password-validation__dialog {
          width: min(500px, 92vw);
          min-height: auto;
          padding: 62px 24px 40px;
        }

        .password-validation__close {
          right: 16px;
        }

        .password-validation__content {
          gap: 18px;
        }

        .password-validation__title {
          font-size: 20px;
          line-height: 26px;
        }

        .password-validation__description,
        .password-validation__recover {
          font-size: 16px;
          line-height: 22px;
        }

        .password-validation__input {
          font-size: 16px;
        }
      }
    `,
  ],
})
export class PasswordValidationModalComponent {
  private readonly theme = inject(ThemeConfigService);

  protected readonly gate = inject(RequireFullAuthService);
  protected readonly brandName = computed(() => this.theme.config().logo.text);
  protected readonly password = signal('');
  protected readonly remember = signal(false);
  protected readonly showPassword = signal(false);

  @ViewChild('passwordInput') private passwordInput?: ElementRef<HTMLInputElement>;

  constructor() {
    effect(() => {
      if (!this.gate.open()) return;
      this.password.set('');
      this.remember.set(false);
      this.showPassword.set(false);
      window.setTimeout(() => this.passwordInput?.nativeElement.focus(), 0);
    });
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.gate.open()) {
      this.gate.cancel('escape');
    }
  }

  protected submit(event: Event): void {
    event.preventDefault();
    void this.gate.confirm(this.password(), this.remember());
  }

  protected setPassword(event: Event): void {
    this.password.set((event.target as HTMLInputElement).value);
  }

  protected setRemember(event: Event): void {
    this.remember.set((event.target as HTMLInputElement).checked);
  }

  protected closeForRecovery(): void {
    this.gate.cancel();
  }
}
